import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@lib/auth';
import { useUIStore } from '@stores/ui.store';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileNav } from './MobileNav';
import { ChangePasswordModal } from './ChangePasswordModal';

export function AuthenticatedLayout(): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  const openQuickLog = useUIStore((state) => state.openQuickLog);
  const quickLogOpen = useUIStore((state) => state.quickLogOpen);

  // Global "Q" shortcut — open the quick log modal from anywhere.
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'q' && event.key !== 'Q') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (quickLogOpen) return;

      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }

      event.preventDefault();
      openQuickLog();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAuthenticated, openQuickLog, quickLogOpen]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-ybh-bg-primary flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-ybh-yellow/30 border-t-ybh-yellow rounded-full animate-spin" />
          <p className="text-ybh-text-secondary text-small">Loading Pulse...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-ybh-bg-primary flex">
      {/* Desktop sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main id="main-content" className="flex-1 p-4 md:p-6 overflow-auto scrollbar-thin pb-20 md:pb-6" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <MobileNav />

      {/* Modals */}
      <ChangePasswordModal />
    </div>
  );
}
